/**
 * Slash-Commands für ChatPage. Deterministisch, kein LLM-Roundtrip.
 * Eingabe `/foo bar` wird hier abgefangen, Ergebnis als persistente
 * Bubble in die Session gespeichert (siehe BuddyPage-Pattern).
 */
import { agentsApi } from "@/features/agents/api"
import { llmModelsApi } from "@/features/llm/api"
import { skillsApi } from "@/features/skills/api"
import { chatApi } from "./api"
import type { AgentBrief, Message, Session } from "./types"

interface ChatCommandResult {
  message: string
  newSessionId?: string
  agentChanged?: AgentBrief
  sessionChanged?: Session
  /** Wenn gesetzt: wird an den Agent geschickt statt lokal angezeigt. */
  sendToAgent?: string
}

const HELP_TEXT = [
  "Verfügbare Befehle in dieser Session:",
  "  /help               — diese Liste",
  "  /clear              — neue Session im selben Projekt + Agent",
  "  /model [name]       — Modell anzeigen oder für diese Session wechseln (/model default = zurück)",
  "  /compact            — manuelle Compaction der Session-History",
  "  /tokens             — Token-Stand + Window-Auslastung",
  "  /title <text>       — Session umbenennen",
  "  /system             — System-Prompt anzeigen",
  "  /tools              — verfügbare Tools im Backend",
  "  /skills             — Skill-Katalog anzeigen",
  "  /agent              — Agent dieser Session anzeigen",
  "  /export             — Verlauf als Markdown ausgeben",
].join("\n")

export function isCommand(text: string): boolean {
  return text.trimStart().startsWith("/")
}

/**
 * /model wirkt wie der Modell-Picker links: nur auf DIESE Session
 * (metadata.model_override). Das Standardmodell des Agenten bleibt unverändert.
 * `/model default` hebt den Session-Override wieder auf.
 */
async function modelCmd(arg: string, agent: AgentBrief, session: Session): Promise<ChatCommandResult> {
  const override = (session.metadata as { model_override?: string } | null)?.model_override
  if (!arg.trim()) {
    const { models } = await llmModelsApi.byModality("chat")
    const ids = models.map((m) => m.id)
    const current = override ? `Aktuell (nur diese Session): ${override} — Agent-Standard: ${agent.llm_model}` : `Aktuell: ${agent.llm_model} (Agent-Standard)`
    return { message: [current, "Verfügbar:", ...ids.map((id) => `  - ${id}`), "", "Wechseln mit `/model <name>` (nur diese Session), zurück mit `/model default`"].join("\n") }
  }
  const target = arg.trim()
  if (target === "default") {
    const updated = await chatApi.updateSession(session.id, { model_override: "" })
    return { message: `Session nutzt wieder das Agent-Modell ${agent.llm_model}.`, sessionChanged: updated }
  }
  const { models } = await llmModelsApi.byModality("chat")
  const ids = models.map((m) => m.id)
  if (ids.length > 0 && !ids.includes(target)) {
    return { message: `Unbekanntes Modell '${target}'. Tippe /model für die Liste.` }
  }
  const updated = await chatApi.updateSession(session.id, { model_override: target })
  return { message: `Modell für diese Session auf ${target} gewechselt. Das Agent-Standardmodell bleibt ${agent.llm_model}.`, sessionChanged: updated }
}

async function clearCmd(session: Session, agent: AgentBrief): Promise<ChatCommandResult> {
  if (session.project_id) await chatApi.handover(session.id)
  const fresh = await chatApi.createSession(agent.id, undefined, session.project_id ?? undefined)
  return { message: `Neue Session gestartet: ${fresh.title || fresh.id.slice(0, 8)}`, newSessionId: fresh.id }
}

async function compactCmd(session: Session): Promise<ChatCommandResult> {
  const r = await chatApi.compact(session.id)
  if (r.skipped) return { message: `Compaction übersprungen (${r.reason_code ?? "unklar"}).` }
  return { message: `Compaction OK: ${r.summarized_count ?? 0} → 1 Summary, ${r.kept_count ?? 0} Messages behalten.` }
}

async function tokensCmd(session: Session): Promise<ChatCommandResult> {
  const r = await chatApi.tokens(session.id)
  const pct = r.context_window ? Math.round((r.used / r.context_window) * 100) : 0
  return { message: [
    `Tokens: ${r.used.toLocaleString()} / ${r.context_window.toLocaleString()} (${pct}%)`,
    `Compact-Threshold: ${r.compact_threshold.toLocaleString()}`,
    `Modell: ${r.model ?? "—"}`,
  ].join("\n") }
}

async function titleCmd(arg: string, session: Session): Promise<ChatCommandResult> {
  const newTitle = arg.trim()
  if (!newTitle) return { message: `Aktueller Titel: ${session.title || "(leer)"}\n\nNutzung: /title <neuer Titel>` }
  const updated = await chatApi.updateSession(session.id, { title: newTitle })
  return { message: `Titel: ${updated.title}`, sessionChanged: updated }
}

async function systemCmd(agent: AgentBrief): Promise<ChatCommandResult> {
  const r = await agentsApi.getSystemPrompt(agent.id)
  return { message: r.prompt ? `System-Prompt:\n\n${r.prompt}` : "System-Prompt ist leer." }
}

async function toolsCmd(): Promise<ChatCommandResult> {
  const tools = await agentsApi.listTools()
  const names = tools.map((t) => `  - ${t.name}`)
  return { message: ["Verfügbare Tools im Backend:", ...names].join("\n") }
}

async function agentCmd(agent: AgentBrief, session: Session): Promise<ChatCommandResult> {
  return { message: [
    `Agent: ${agent.name}  (id ${agent.id.slice(0, 8)}…)`,
    `Modell: ${agent.llm_model}`,
    `Session: ${session.title || session.id.slice(0, 8)}`,
  ].join("\n") }
}

async function skillsCmd(agent: AgentBrief): Promise<ChatCommandResult> {
  const skills = await skillsApi.list({ agentId: agent.id })
  if (skills.length === 0) return { message: "Keine Skills verfügbar." }
  const rows = skills.map((s) =>
    `| \`${s.name}\` | ${s.description || "—"} |`
  )
  return {
    message: [
      `## Skills (${skills.length})`,
      "",
      "| Name | Beschreibung |",
      "|------|-------------|",
      ...rows,
      "",
      "Skill nutzen: _Nutze den Skill \"name\"_",
    ].join("\n"),
  }
}

function exportCmd(messages: Message[]): ChatCommandResult {
  const lines: string[] = ["# Chat-Export", ""]
  for (const m of messages) {
    if (m.role === "compaction") continue
    const role = m.role === "user" ? "User" : m.role === "assistant" ? "Assistant" : m.role
    lines.push(`## ${role} — ${m.created_at}`)
    if (typeof m.content === "string") lines.push(m.content)
    else if (!Array.isArray(m.content)) { /* null — skip */ }
    else for (const b of m.content) {
      if (b.type === "text") lines.push(b.text)
      else if (b.type === "tool_use") lines.push(`\`tool_use: ${b.name}\` ${JSON.stringify(b.input)}`)
      else if (b.type === "tool_result") lines.push(`\`tool_result\` ${typeof b.content === "string" ? b.content : JSON.stringify(b.content)}`)
    }
    lines.push("")
  }
  return { message: lines.join("\n") }
}

export async function runChatCommand(
  text: string,
  session: Session,
  agent: AgentBrief,
  messages: Message[],
): Promise<ChatCommandResult> {
  const trimmed = text.trim()
  const space = trimmed.indexOf(" ")
  const cmd = (space === -1 ? trimmed : trimmed.slice(0, space)).toLowerCase()
  const arg = space === -1 ? "" : trimmed.slice(space + 1)
  try {
    switch (cmd) {
      case "/help": return { message: HELP_TEXT }
      case "/clear": case "/reset": return await clearCmd(session, agent)
      case "/model": case "/models": return await modelCmd(arg, agent, session)
      case "/compact": return await compactCmd(session)
      case "/tokens": return await tokensCmd(session)
      case "/title": case "/rename": return await titleCmd(arg, session)
      case "/system": case "/sys": return await systemCmd(agent)
      case "/tools": return await toolsCmd()
      case "/agent": return await agentCmd(agent, session)
      case "/skills": case "/skillkatalog": return await skillsCmd(agent)
      case "/export": return exportCmd(messages)
      default: {
        const skillName = cmd.slice(1)
        const skills = await skillsApi.list({ agentId: agent.id })
        const skill = skills.find((s) => s.name === skillName)
        if (skill) {
          return {
            message: "",
            sendToAgent: `Führe den Skill "${skill.name}" aus.\n\n${skill.body}${arg ? `\n\nKontext: ${arg}` : ""}`,
          }
        }
        return { message: `Unbekannter Befehl ${cmd}. Tippe /help oder /skills für den Skill-Katalog.` }
      }
    }
  } catch (e) {
    return { message: `Fehler: ${e instanceof Error ? e.message : String(e)}` }
  }
}
